// The Vue build version to load with the `import` command
// (runtime-only or standalone) has been set in webpack.base.conf with an alias.
import 'babel-polyfill';
import Vue from 'vue';
import Vuex from 'vuex';
import Vuetify from 'vuetify';
import App from './App';
import router from './router';
import store from '@/vuex';
import moment from 'moment';
import VueChartkick from 'vue-chartkick';
import Chart from 'chart.js';
import isEmpty from 'lodash/isEmpty';
import 'vuetify/dist/vuetify.min.css';
import './assets/style/vuetify-overrides.css';
import './styles/globals.css';
import Vue2Filters from 'vue2-filters';
import VueAxios from 'vue-axios';
import Vuelidate from 'vuelidate';
import Quill from 'quill';
import VueQuillEditor from 'vue-quill-editor';
import axios from 'axios';
import '@/firebase/';
import firebase from 'firebase/app';
import * as Sentry from '@sentry/vue';
import { BrowserTracing } from '@sentry/tracing';
import upperFirst from 'lodash/upperFirst';
import camelCase from 'lodash/camelCase';
import 'quill/dist/quill.core.css';
import 'quill/dist/quill.snow.css';
import parse from 'url-parse';
import VueI18n from 'vue-i18n';
import { languages } from './translations';
// import posthog from 'posthog-js';
import VueCompositionAPI from '@vue/composition-api';
import VueSignaturePad from 'vue-signature-pad';
import VueVirtualScroller from 'vue-virtual-scroller';
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';
import VueMask from 'v-mask';

let Link = Quill.import('formats/link');
Link.sanitize = function customSanitizeLinkInput(linkValueInput) {
  let val = linkValueInput;
  if (/^\w+:/.test(val));
  else if (!/^https?:/.test(val)) val = 'https://' + val;

  return val;
};
window.Quill = Quill;

Vue.use(VueCompositionAPI);
Vue.use(VueSignaturePad);
Vue.use(VueVirtualScroller);
Vue.use(VueMask);

Vue.prototype.$firebase = firebase;

Vue.mixin({
  computed: {
    isEmbeddedInNext: () => {
      return window.isEmbeddedInNext;
    },
    isInIframe: () => {
      return window.self !== window.top;
    },
  },
});

moment.locale('en', {
  relativeTime: {
    future: 'in %s',
    past: '%s ago',
    s: 'a few seconds',
    ss: '%d seconds',
    m: 'a minute',
    mm: '%d minutes',
    h: 'an hour',
    hh: '%d hours',
    d: 'a day',
    dd: '%d days',
    w: 'a week',
    ww: '%d weeks',
    M: 'a month',
    MM: '%d months',
    y: 'a year',
    yy: '%d years',
  },
});

// Posthog
// posthog.init('phc_LdizL8pP1phIt73FCXCK9uuYnQ0R2JluBRyY33ZL1nZ', {
//   api_host: 'https://app.posthog.com',
//   maskAllInputs: true,
//   maskInputFn: (text, _element) => '*'.repeat(text.length),
// });

// Sentry Error Tracking
Sentry.init({
  Vue,
  dsn: process.env.SENTRY_DSN,
  integrations: [
    new BrowserTracing({
      routingInstrumentation: Sentry.vueRouterInstrumentation(router),
      tracePropagationTargets: ['localhost', 'soraban.com', /^\//],
    }),
  ],
  // Attach stacktraces to messages as well as exceptions
  attachStacktrace: true,
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 1.0,
});

Vue.use(Vuelidate);
Vue.use(Vuex);
Vue.use(VueAxios, axios);
Vue.use(VueQuillEditor);

Vue.config.productionTip = process.env.NODE_ENV === 'production';
// NOTE: uncomment this if you're getting way too many warnings in the console
// Vue.config.silent = true;
Vue.use(Vue2Filters);
Vue.filter('date', date => moment(date).format('LLL'));
Vue.filter('dateOnly', date => moment(date).format('LL'));

Vue.use(VueChartkick, { adapter: Chart });
Vue.use(Vuetify, {
  theme: {
    primary: '#136DF4',
    secondary: '#6F839C',
    darkgrey: '#616161',
    darkyellow: '#CDCD00',
    accent: '#4F7EEA',
    error: '#FA4C4F',
    success: '#64BE79',
    warning: '#FABE01',
    'green-accent': '#24ccb8',
  },
});

Vue.filter('kb', val => {
  return Math.floor(val / 1024);
});

Vue.use(VueI18n);

const requireComponent = require.context(
  // The relative path of the components folder
  './components/global',
  // Whether or not to look in subfolders
  false,
  // The regular expression used to match base component filenames
  /Soraban[A-Z]\w+\.(vue)$/,
);

requireComponent.keys().forEach(fileName => {
  // Get component config
  const componentConfig = requireComponent(fileName);

  // Get PascalCase name of component
  const componentName = upperFirst(
    camelCase(
      // Gets the file name regardless of folder depth
      fileName
        .split('/')
        .pop()
        .replace(/\.\w+$/, ''),
    ),
  );

  // Register component globally
  Vue.component(
    componentName,
    // Look for the component options on `.default`, which will
    // exist if the component was exported with `export default`,
    // otherwise fall back to module's root.
    componentConfig.default || componentConfig,
  );
});

router.beforeEach(async (to, from, next) => {
  if (to.meta.sorabanOnly) {
    const firebaseUser = await store.dispatch('getFirebaseUser');
    const workosAuth = await ensureWorkOSSession(to);

    if (isEmpty(firebaseUser) && !workosAuth.hasWorkOSAuth) {
      next({
        path: '/signin',
        query: {
          redirect: to.fullPath,
          email: to.query.email,
        },
      });
    } else {
      const currentUser = workosAuth.hasWorkOSAuth
        ? workosAuth.storeUser
        : await store.dispatch('getCurrentUser');

      loadHubSpotWidget(currentUser);

      const email = (currentUser.email || '').toLowerCase();
      if (currentUser.is_advisor && email.includes('@soraban.com')) {
        next();
      } else {
        store.dispatch('application/setErrorNotification', {
          message: "You don't have access to that page.",
        });
        next('/overview');
      }
    }
  } else if (to.meta.superAdminOnly) {
    // super admin pages
    const firebaseUser = await store.dispatch('getFirebaseUser');
    const workosAuth = await ensureWorkOSSession(to);

    if (isEmpty(firebaseUser) && !workosAuth.hasWorkOSAuth) {
      next({
        path: '/signin',
        query: {
          redirect: to.fullPath,
          email: to.query.email,
        },
      });
    } else {
      const currentUser = workosAuth.hasWorkOSAuth
        ? workosAuth.storeUser
        : await store.dispatch('getCurrentUser');

      loadHubSpotWidget(currentUser);

      if (currentUser.is_advisor && currentUser.email_verified) {
        next();
      } else {
        store.dispatch('application/setErrorNotification', {
          message: "You don't have access to that page.",
        });
        next('/overview');
      }
    }
  } else if (
    to.matched.some(record => record.meta.requiresAuth && record.meta.advisor) // advisor pages
  ) {
    // Wait for iframe auth to complete if it's running
    await iframeAuthPromise;

    const firebaseUser = await store.dispatch('getFirebaseUser');
    const workosAuth = await ensureWorkOSSession(to);

    if (isEmpty(firebaseUser) && !workosAuth.hasWorkOSAuth) {
      next({
        path: '/signin',
        query: {
          redirect: to.fullPath,
          email: to.query.email,
        },
      });
    } else {
      const currentUser = workosAuth.hasWorkOSAuth
        ? workosAuth.storeUser
        : await store.dispatch('getCurrentUser');

      loadHubSpotWidget(currentUser);

      if (currentUser.is_advisor) {
        next();
      } else {
        store.dispatch('application/setErrorNotification', {
          message: "You don't have access to that page.",
        });
        next('/');
      }
    }
  } else if (to.meta.requiresAuth) {
    // member pages
    // Wait for iframe auth to complete if it's running
    await iframeAuthPromise;

    const firebaseUser = await store.dispatch('getFirebaseUser');
    const workosAuth = await ensureWorkOSSession(to);

    if (isEmpty(firebaseUser) && !workosAuth.hasWorkOSAuth) {
      next({
        path: '/signin',
        query: {
          redirect: to.fullPath,
          email: to.query.email,
        },
      });
    } else {
      const currentUser = workosAuth.hasWorkOSAuth
        ? workosAuth.storeUser
        : await store.dispatch('getCurrentUser');
      if (currentUser.is_advisor) {
        if (to.path === '/') {
          next({ path: '/overview' });
        }
        if (window.Intercom) {
          window.Intercom('update');
        }
      }

      next();
    }
  } else {
    next();
  }
});

// Lazy-load companies when navigating from deliver routes to non-deliver routes
router.beforeEach(async (to, from, next) => {
  const fromSkipRoute = from.meta && from.meta.skipCompaniesLoad;
  const toSkipRoute = to.meta && to.meta.skipCompaniesLoad;
  const companiesLoaded = store.state.companies.companies.length > 0;

  if (
    fromSkipRoute &&
    !toSkipRoute &&
    !companiesLoaded &&
    store.getters.isAuthenticated
  ) {
    console.log(
      '[Router] Lazy-loading companies on navigation from deliver route',
    );

    try {
      await store.dispatch('companies/getCompanies');

      const user = store.getters.user;
      if (user.default_company_id) {
        await store.dispatch('setDefaultCompany', {
          companyId: user.default_company_id,
        });
      }
    } catch (error) {
      console.error('[Router] Failed to lazy-load companies:', error);
    }
  }

  next();
});

let app;

let workOSBootstrapPromise = null;

async function buildWorkOSSession(destinationRoute = null) {
  const storedToken = localStorage.getItem('firebase__authToken');
  const hasWorkOSToken = storedToken && storedToken.startsWith('Bearer ');

  let storeUser = store.getters.user;

  if (!hasWorkOSToken) {
    return {
      storedToken,
      hasWorkOSToken,
      storeUser,
      hasWorkOSAuth: false,
    };
  }

  if (!storeUser || isEmpty(storeUser)) {
    try {
      storeUser = await store.dispatch('getCurrentUser');
    } catch (error) {
      console.error('[WorkOS] Failed to fetch current user:', error);
      storeUser = {};
    }
  }

  let bootstrappedUser = null;
  try {
    bootstrappedUser = await bootstrapUserSession(storeUser, destinationRoute);
  } catch (error) {
    console.error('[WorkOS] Failed to bootstrap session:', error);
  }

  return {
    storedToken,
    hasWorkOSToken,
    storeUser: bootstrappedUser || storeUser,
    hasWorkOSAuth: Boolean(
      hasWorkOSToken && bootstrappedUser && !isEmpty(bootstrappedUser),
    ),
  };
}

function ensureWorkOSSession(destinationRoute = null) {
  if (!workOSBootstrapPromise) {
    const pendingSession = buildWorkOSSession(destinationRoute);
    workOSBootstrapPromise = pendingSession
      .then(result => {
        workOSBootstrapPromise = null;
        return result;
      })
      .catch(error => {
        workOSBootstrapPromise = null;
        throw error;
      });
  }

  return workOSBootstrapPromise;
}

async function bootstrapUserSession(user, destinationRoute = null) {
  if (!user || isEmpty(user)) {
    return null;
  }

  const normalizedUser = {
    ...user,
    overview_setting: user.overview_setting || {},
  };

  store.commit('setUser', normalizedUser);

  let currentFirm = null;

  try {
    currentFirm = await store.dispatch('setCurrentFirm');
  } catch (error) {
    console.error('Failed to set current firm:', error);
  }

  // Conditionally skip companies loading for deliver routes
  // Check both route meta AND URL path (in case router hasn't navigated yet)
  const skipCompaniesRoutePaths = [
    '/tax-return-delivery',
    '/questionnaire',
    '/questionnaire-templates',
    '/signature-request',
    '/review-signature-request',
    '/k1-package',
    '/filing-instructions',
  ];

  const currentPath = window.location.pathname;
  const pathShouldSkip = skipCompaniesRoutePaths.some(path =>
    currentPath.startsWith(path),
  );

  const shouldSkipCompanies =
    (destinationRoute &&
      destinationRoute.meta &&
      destinationRoute.meta.skipCompaniesLoad) ||
    pathShouldSkip;

  if (!shouldSkipCompanies) {
    try {
      await store.dispatch('companies/getCompanies');
    } catch (error) {
      console.error('Failed to load companies:', error);
    }
  } else {
    console.log(
      '[Bootstrap] Skipping companies load for deliver/embedded route:',
      currentPath,
    );
  }

  // Only set default company if companies were loaded
  if (!shouldSkipCompanies && normalizedUser.default_company_id) {
    try {
      await store.dispatch('setDefaultCompany', {
        companyId: normalizedUser.default_company_id,
      });
    } catch (error) {
      console.error('Failed to set default company:', error);
    }
  }

  try {
    await store.dispatch('resetTiles', currentFirm);
  } catch (error) {
    console.error('Failed to reset tiles:', error);
  }

  if (!normalizedUser.is_advisor && currentFirm && currentFirm.theme) {
    Vue.prototype.$vuetify.theme = Object.assign(
      Vue.prototype.$vuetify.theme,
      currentFirm.theme,
    );
  }

  return normalizedUser;
}

// Initialize authentication for iframe contexts with WorkOS Bearer tokens
async function initializeIframeAuth() {
  const isInIframe = window.self !== window.top;
  const isEmbeddedInNext = window.isEmbeddedInNext;
  const storedToken = localStorage.getItem('firebase__authToken');

  if (
    (isInIframe || isEmbeddedInNext) &&
    storedToken &&
    storedToken.startsWith('Bearer ')
  ) {
    try {
      const workosAuth = await ensureWorkOSSession(router.currentRoute);
      const user = workosAuth.storeUser;

      if (user && !isEmpty(user)) {
        // Initialize the Vue app if it doesn't exist yet
        if (!app) {
          const selectedLanguage =
            user && user.language ? user.language : navigator.language;

          const i18n = new VueI18n({
            locale: selectedLanguage,
            messages: languages,
            silentTranslationWarn: true,
          });

          app = new Vue({
            el: '#app',
            i18n,
            router,
            store,
            components: { App },
            template: '<App/>',
          });
        } else {
          // Check if we need to navigate to the correct route
          if (router.currentRoute.path !== window.location.pathname) {
            // Use full path with query parameters to preserve ?companyId=232212
            const fullPath = window.location.pathname + window.location.search;
            router.push(fullPath);
          } else {
            // Force a route refresh to trigger auth checks
            router.push(router.currentRoute.path);
          }
        }
      }
    } catch (error) {
      console.error('Failed to initialize user state:', error);
    }
  }
}

// Call initialization for iframe contexts immediately and wait for completion
let iframeAuthPromise = initializeIframeAuth().catch(error => {
  console.error('initializeIframeAuth threw error:', error);
});

firebase.auth().onAuthStateChanged(async currentUser => {
  let user;

  if (!isEmbedded()) {
    const storedToken = localStorage.getItem('firebase__authToken');
    const hasWorkOSToken = storedToken && storedToken.startsWith('Bearer ');

    if (currentUser) {
      await store.dispatch('getFirebaseUser');
      const token = await store.dispatch('getToken');
      if (token) {
        const fetchedUser = await store.dispatch('getCurrentUser');
        user = await bootstrapUserSession(fetchedUser, router.currentRoute);

        if (!user) {
          store.dispatch('userSignOut');
        }
      }
    } else if (hasWorkOSToken) {
      try {
        const workosAuth = await ensureWorkOSSession(router.currentRoute);
        user = workosAuth.storeUser;

        if (!user) {
          store.dispatch('userSignOut');
        }
      } catch (error) {
        console.error(
          '[WorkOS] Failed to initialize session from token:',
          error,
        );
        store.dispatch('userSignOut');
      }
    } else {
      store.dispatch('userSignOut');
    }
  }

  const selectedLanguage =
    user && user.language ? user.language : navigator.language;
  if (!app) {
    const i18n = new VueI18n({
      locale: selectedLanguage,
      messages: languages,
      silentTranslationWarn: true,
    });

    /* eslint-disable no-new */
    app = new Vue({
      el: '#app',
      i18n,
      router,
      store,
      components: { App },
      template: '<App/>',
    });
  } else {
    app.$i18n.locale = selectedLanguage;
  }
});

function isEmbedded() {
  const parsed = parse(window.location.href);

  return parsed.pathname === '/embed';
}

function loadHubSpotWidget(currentUser) {
  if (
    window.innerWidth >= 1000 &&
    currentUser.is_advisor &&
    !window.isEmbeddedInNext
  ) {
    var script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.defer = true;
    script.id = 'hs-script-loader';
    script.src = '//js-na1.hs-scripts.com/44838654.js';

    var firstScript = document.getElementsByTagName('script')[0];
    firstScript.parentNode.insertBefore(script, firstScript);

    script.onload = function() {
      if (window.HubSpotConversations) {
        window.HubSpotConversations.widget.load();
      }
    };
  }
}
